import {
  ChangeDetectionStrategy,
  Component,
  Signal,
  computed,
  inject,
  input,
  output,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AiScribeSessionService } from '@principle-theorem/ng-ai-scribe';
import { FeatureFlagsService } from '@principle-theorem/ng-feature-flags';
import {
  ManagementService,
  OrganisationService,
} from '@principle-theorem/ng-principle-shared';
import { DialogPresets, NgCommonModule } from '@principle-theorem/ng-shared';
import {
  AiScribeSeats,
  ScribeSession,
  stafferToNamedDoc,
} from '@principle-theorem/principle-core';
import {
  AI_NOTES_FEATURE,
  AI_SCRIBE_FEATURE,
} from '@principle-theorem/principle-core/features';
import {
  IPatient,
  IScribeSession,
  IStaffer,
} from '@principle-theorem/principle-core/interfaces';
import { ISODateType, WithRef } from '@principle-theorem/shared';
import { Observable, combineLatest, of } from 'rxjs';
import { map, scan, switchMap, tap } from 'rxjs/operators';
import { AiNotesButtonComponent } from '../ai-notes/ai-notes-button/ai-notes-button.component';
import {
  IScribeReviewDialogData,
  IScribeReviewDialogResult,
  ScribeReviewDialogComponent,
} from '../scribe-review-dialog/scribe-review-dialog.component';

interface IScribeLauncherView {
  showHeidi: boolean;
  showRecord: boolean;
}

/**
 * One emission of the bound key's ready-session query, carrying the sessions
 * that have appeared since the key's FIRST emission. The first emission only
 * establishes the baseline: everything already ready when the chart (or the
 * Recording As / date key) loaded is pre-seen, so it never auto-opens.
 */
interface IReadySessionScan {
  sessions: WithRef<IScribeSession>[];
  seenSessionIds: ReadonlySet<string>;
  appeared: WithRef<IScribeSession>[];
  baselined: boolean;
}

const READY_SESSION_SCAN_SEED: IReadySessionScan = {
  sessions: [],
  seenSessionIds: new Set<string>(),
  appeared: [],
  baselined: false,
};

/** Tooltip/aria label on the button once it is a Record-or-View menu trigger. */
const SCRIBE_MENU_LABEL = 'Record or view Scribe Notes';

/** Tooltip shown while a live session blocks starting another recording. */
const SCRIBE_SESSION_IN_PROGRESS_LABEL = 'Session in progress';

/**
 * The single UI seam that lets Heidi and AI Scribe coexist. The two affordances
 * are gated independently and may render side by side: Heidi's account button
 * follows the organisation's `ai-notes` opt-in alone, while the AI Scribe icon
 * "Record" button follows the `ai-scribe` opt-in plus the signed-in staffer's
 * AI Scribe Seat — with management users bypassing the seat so they can test
 * without one. The parent keeps binding `[patient]` and handling
 * `(noteCreated)` unchanged, so the existing Heidi note pipeline is preserved
 * while AI Scribe is trialled alongside it.
 *
 * When AI Scribe is active this button also owns the review affordance, so the
 * chart carries ONE scribe control rather than a record button beside a second
 * sessions button. It badges the pending (recorded-but-not-inserted) note count
 * for the bound patient + staffer + chart day, and — once that day has produced
 * any notes at all — becomes a menu offering "Record note" and "View notes".
 * The two predicates differ on purpose: a day whose notes are all inserted shows
 * no badge yet must still be reachable for re-reading and re-insertion.
 *
 * Counts come from the session counters alone; the notes subcollection is read
 * only inside the review dialog this menu opens.
 *
 * It also owns the on-context auto-open (ADR-0004: opened for review, never
 * auto-inserted). The launcher is the only surface that knows all three parts of
 * the key a note belongs to, so a session that becomes ready while the clinician
 * is on that exact patient + Recording As + chart day opens the review dialog
 * itself, focused on that session. The recorder keeps only the off-context
 * fallback, which is patient-keyed.
 */
@Component({
  selector: 'pr-scribe-launcher',
  imports: [
    NgCommonModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatTooltipModule,
    AiNotesButtonComponent,
  ],
  templateUrl: './scribe-launcher.component.html',
  styleUrl: './scribe-launcher.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScribeLauncherComponent {
  private readonly _featureFlags = inject(FeatureFlagsService);
  private readonly _organisation = inject(OrganisationService);
  private readonly _management = inject(ManagementService);
  private readonly _scribe = inject(AiScribeSessionService);
  private readonly _dialog = inject(MatDialog);
  private readonly _sessions: Signal<WithRef<IScribeSession>[]>;
  private readonly _view: Signal<IScribeLauncherView | undefined>;
  /**
   * Set synchronously before the auto-open awaits its lazy `import()`, because
   * `MatDialog.openDialogs` cannot yet reflect a dialog this instance has only
   * decided to open. Re-entrancy within ONE instance is all it guards; two
   * mounted launchers are deduped by re-reading `openDialogs` after the import.
   */
  private _autoOpening = false;

  readonly patient = input<WithRef<IPatient>>();
  /**
   * The "Recording As" clinician the started session is attributed to, and the
   * charted day it is filed under. Both arrive from async host bindings, so they
   * are transiently undefined and the Record button stays disabled until they
   * resolve — never falling back to the signed-in staffer or today. Heidi's
   * button is deliberately unaffected by either.
   */
  readonly staffer = input<WithRef<IStaffer>>();
  readonly recordDate = input<ISODateType>();
  readonly noteCreated = output<string>();
  /**
   * Which affordances to render — independently gated, so both may be true.
   * Both false until the organisation, signed-in staffer and management
   * context resolve.
   */
  readonly showHeidi: Signal<boolean>;
  readonly showRecord: Signal<boolean>;
  readonly menuLabel = SCRIBE_MENU_LABEL;
  readonly sessionInProgressLabel = SCRIBE_SESSION_IN_PROGRESS_LABEL;

  /**
   * True while this staffer already has a live session running. `startSession`
   * silently refuses a second one, so the button reflects that refusal rather
   * than swallowing the click.
   */
  readonly sessionInProgress = this._scribe.sessionInProgress;

  /**
   * Whether the bound "Recording As" staffer and chart day have both resolved —
   * the context a Record or a review is keyed on. Kept separate from
   * `recordDisabled` so a live session, which blocks a NEW recording, still
   * leaves the review menu reachable rather than collapsing it to a plain
   * disabled button.
   */
  readonly contextResolved = computed(
    () => this.staffer() !== undefined && this.recordDate() !== undefined
  );

  readonly recordDisabled = computed(
    () => !this.contextResolved() || this.sessionInProgress()
  );

  /** Recorded-but-not-yet-inserted notes for the bound day — the badge. */
  readonly pendingNoteCount: Signal<number>;
  /** Every note the bound day produced, already-inserted ones included. */
  readonly noteCount: Signal<number>;

  /**
   * Whether the button becomes a Record/View menu instead of a plain record
   * button. Gated on the resolved context so an unresolved staffer or chart day
   * can never offer a review of someone else's day; and on the day having
   * produced notes at all, which is deliberately a looser test than the badge's
   * pending count. Deliberately independent of `sessionInProgress` so a live
   * session leaves the menu (and its View notes item) reachable.
   */
  readonly showMenu: Signal<boolean>;

  constructor() {
    this._view = toSignal(
      combineLatest([
        this._featureFlags.isFeatureEnabled$(AI_NOTES_FEATURE),
        this._featureFlags.isFeatureEnabled$(AI_SCRIBE_FEATURE),
        this._organisation.staffer$,
        this._management.user$,
      ]).pipe(
        map(([aiNotesEnabled, aiScribeEnabled, staffer, managementUser]) =>
          this._resolveView(
            aiNotesEnabled,
            aiScribeEnabled,
            staffer,
            !!managementUser
          )
        )
      )
    );
    this.showHeidi = computed(() => this._view()?.showHeidi ?? false);
    this.showRecord = computed(() => this._view()?.showRecord ?? false);

    this._sessions = toSignal(
      combineLatest([
        toObservable(this.patient),
        toObservable(this.staffer),
        toObservable(this.recordDate),
      ]).pipe(
        switchMap(([patient, staffer, recordDate]) =>
          this._readyForContext$(patient, staffer, recordDate)
        )
      ),
      { initialValue: [] as WithRef<IScribeSession>[] }
    );

    this.pendingNoteCount = computed(() =>
      this._sessions().reduce(
        (total, session) => total + ScribeSession.pendingNoteCount(session),
        0
      )
    );

    this.noteCount = computed(
      () => ScribeSession.counterSummary(this._sessions()).notesGenerated
    );

    this.showMenu = computed(
      () => this.contextResolved() && this.noteCount() > 0
    );
  }

  /**
   * Start a live AI Scribe session for the bound patient, attributed to the
   * bound "Recording As" staffer on the bound charted date. The floating
   * recorder (mounted in the app shell) surfaces the live session, so nothing is
   * rendered here beyond kicking off the session.
   */
  startRecording(): void {
    const patient = this.patient();
    const staffer = this.staffer();
    const recordDate = this.recordDate();
    if (
      patient === undefined ||
      staffer === undefined ||
      recordDate === undefined
    ) {
      return;
    }
    void this._scribe.startSession(patient, staffer, recordDate);
  }

  /**
   * Open the review dialog on the same patient + staffer + chart day the button
   * is bound to, so the dialog resolves the day's sessions itself and every
   * entry point lands on the identical view. A no-op while any of the three
   * are unresolved, which is the state `showMenu()` already suppresses.
   */
  openReview(): void {
    const patient = this.patient();
    const staffer = this.staffer();
    const recordDate = this.recordDate();
    if (
      patient === undefined ||
      staffer === undefined ||
      recordDate === undefined
    ) {
      return;
    }
    this._dialog.open<
      ScribeReviewDialogComponent,
      IScribeReviewDialogData,
      IScribeReviewDialogResult
    >(
      ScribeReviewDialogComponent,
      DialogPresets.extraLarge<IScribeReviewDialogData>({
        data: {
          patient,
          staffer: stafferToNamedDoc(staffer),
          recordDate,
        },
      })
    );
  }

  /**
   * The bound day's ready sessions, or nothing at all while an input is still
   * unresolved — the launcher never falls back to the signed-in staffer or to
   * today, so it cannot briefly badge another clinician's notes.
   *
   * The unresolved-input `of([])` is a GUARD emission, not data: it is returned
   * before the fold below is ever created, so it can neither establish a
   * baseline nor look like a session appearing. The same is true of the
   * `initialValue: []` seeding the signal.
   */
  private _readyForContext$(
    patient: WithRef<IPatient> | undefined,
    staffer: WithRef<IStaffer> | undefined,
    recordDate: ISODateType | undefined
  ): Observable<WithRef<IScribeSession>[]> {
    if (
      patient === undefined ||
      staffer === undefined ||
      recordDate === undefined
    ) {
      return of([]);
    }
    return ScribeSession.readyForContext$(
      patient,
      staffer.ref,
      recordDate
    ).pipe(
      scan(
        (previous, sessions) => this._scanAppearedSessions(previous, sessions),
        READY_SESSION_SCAN_SEED
      ),
      tap((emission) =>
        emission.appeared.forEach((session) =>
          this._autoOpenReview(patient, staffer, recordDate, session.ref.id)
        )
      ),
      map((emission) => emission.sessions)
    );
  }

  /**
   * Opens the review dialog for a session that became ready while the clinician
   * was already on its key, focused on that session. Never stacks: the dialog
   * component is pulled in lazily, so `openDialogs` is checked once before the
   * `import()` and again after it resolves — without the second check the await
   * gap would let two mounted launchers (the note editor dialog and the outgoing
   * patient sidebar both host one) each see an empty stack and open.
   */
  private _autoOpenReview(
    patient: WithRef<IPatient>,
    staffer: WithRef<IStaffer>,
    recordDate: ISODateType,
    initialSessionId: string
  ): void {
    if (this._autoOpening || this._dialog.openDialogs.length > 0) {
      return;
    }
    this._autoOpening = true;
    void this._openReviewDialog({
      patient,
      staffer: stafferToNamedDoc(staffer),
      recordDate,
      initialSessionId,
    });
  }

  private async _openReviewDialog(
    data: IScribeReviewDialogData
  ): Promise<void> {
    try {
      const { ScribeReviewDialogComponent: dialogComponent } = await import(
        '../scribe-review-dialog/scribe-review-dialog.component'
      );
      if (this._dialog.openDialogs.length > 0) {
        return;
      }
      this._dialog.open<
        ScribeReviewDialogComponent,
        IScribeReviewDialogData,
        IScribeReviewDialogResult
      >(
        dialogComponent,
        DialogPresets.extraLarge<IScribeReviewDialogData>({ data })
      );
    } finally {
      this._autoOpening = false;
    }
  }

  /**
   * Folds each emission of one key's ready-session query into the set of
   * sessions seen so far, reporting only genuinely new ids. Because the fold is
   * recreated per key (the `switchMap` resubscribes), switching Recording As or
   * chart day re-baselines from scratch rather than treating the new key's
   * existing notes as newly arrived.
   */
  private _scanAppearedSessions(
    previous: IReadySessionScan,
    sessions: WithRef<IScribeSession>[]
  ): IReadySessionScan {
    if (!previous.baselined) {
      return {
        sessions,
        seenSessionIds: new Set(sessions.map((session) => session.ref.id)),
        appeared: [],
        baselined: true,
      };
    }
    const appeared = sessions.filter(
      (session) => !previous.seenSessionIds.has(session.ref.id)
    );
    return {
      sessions,
      seenSessionIds: new Set([
        ...previous.seenSessionIds,
        ...appeared.map((session) => session.ref.id),
      ]),
      appeared,
      baselined: true,
    };
  }

  private _resolveView(
    aiNotesEnabled: boolean,
    aiScribeEnabled: boolean,
    staffer: WithRef<IStaffer> | undefined,
    isManagement: boolean
  ): IScribeLauncherView {
    return {
      showHeidi: aiNotesEnabled,
      showRecord: AiScribeSeats.isAiScribeActive(
        aiScribeEnabled,
        staffer,
        isManagement
      ),
    };
  }
}
